import {inputAnatomy as parts} from '@chakra-ui/anatomy';
import {getToken} from '@chakra-ui/react';
import {createMultiStyleConfigHelpers, cssVar, defineStyle} from '@chakra-ui/styled-system';
import {getColorVar, mode} from '@chakra-ui/theme-tools';

import {components as darkComponents} from '../../design-tokens/dark';
import {components} from '../../design-tokens/light';

const lightColors = components.colors.components[`form-control`];
const darkColors = darkComponents.colors.components[`form-control`];

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts.keys);

export const $height = cssVar(`input-height`);
export const $fontSize = cssVar(`input-font-size`);
export const $padding = cssVar(`input-padding`);
export const $borderRadius = cssVar(`input-border-radius`);

const baseStyle = definePartsStyle({
  addon: {
    height: $height.reference,
    fontSize: $fontSize.reference,
    px: $padding.reference,
    borderRadius: $borderRadius.reference,
  },
  field: {
    width: `full`,
    height: $height.reference,
    fontSize: $fontSize.reference,
    px: $padding.reference,
    borderRadius: $borderRadius.reference,
    minWidth: 0,
    outline: 0,
    position: `relative`,
    appearance: `none`,
    transitionProperty: `common`,
    transitionDuration: `normal`,
    _disabled: {
      opacity: 1,
      cursor: `not-allowed`,
    },
    _placeholder: {
      opacity: 0.5,
    },
  },
});

const size = {
  lg: defineStyle({
    [$fontSize.variable]: `fontSizes.md`,
    [$padding.variable]: `space.space-150`,
    [$borderRadius.variable]: `radii.radius-100`,
    [$height.variable]: `sizes.space-600`, // 48px
  }),
  md: defineStyle({
    [$fontSize.variable]: `fontSizes.sm`,
    [$padding.variable]: `space.space-100`,
    [$borderRadius.variable]: `radii.radius-050`,
  }),
  sm: defineStyle({
    [$height.variable]: `space.space-300`, // 24px
    [$padding.variable]: `0`,
    [$borderRadius.variable]: `radii.radius-100`,
    lineHeight: 1,
  }),
};

const sizes = {
  lg: definePartsStyle({
    field: size.lg,
    group: size.lg,
  }),
  md: definePartsStyle({
    field: size.md,
    group: size.md,
  }),
  sm: definePartsStyle({
    field: size.sm,
    group: size.sm,
  }),
};

const variantOutline = definePartsStyle(props => {
  const focusBorderToken = mode(lightColors[`stroke-focus`], darkColors[`stroke-focus`])(props);
  const focusBorderColor = getToken(`colors`, focusBorderToken)(props.theme);

  return {
    field: {
      borderWidth: `1px`,
      borderStyle: `solid`,
      color: mode(lightColors[`text-default`], darkColors[`text-default`])(props),
      borderColor: mode(lightColors[`stroke-default`], darkColors[`stroke-default`])(props),
      bg: mode(lightColors[`background-default`], darkColors[`background-default`])(props),
      _hover: {
        borderColor: mode(lightColors[`stroke-hover`], darkColors[`stroke-hover`])(props),
      },
      _readOnly: {
        boxShadow: `none !important`,
        userSelect: `all`,
      },
      _invalid: {
        color: mode(lightColors[`text-error`], darkColors[`text-error`])(props),
        borderColor: mode(lightColors[`stroke-error`], darkColors[`stroke-error`])(props),
        boxShadow: `none`,
        bg: getColorVar(props.theme, `background.state.danger-subtle`),
      },
      _focusVisible: {
        zIndex: 1,
        borderColor: focusBorderToken,
        boxShadow: `0 0 0 1px ${focusBorderColor}`,
      },
    },
    addon: {
      borderWidth: `1px`,
      borderStyle: `solid`,
      borderColor: mode(lightColors[`stroke-default`], darkColors[`stroke-default`])(props),
      boxShadow: `none`,
      bg: mode(lightColors[`background-default`], darkColors[`background-default`])(props),
    },
  };
});

const variants = {
  outline: variantOutline,
};

export const inputTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants,
  defaultProps: {
    size: `lg`,
    variant: `outline`,
  },
});
